import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Logo from './Logo';
import AuthModal from './AuthModal';

/**
 * Kurulum kısıtlıyken oyuncu portalının yerine çizilen tam ekran duyuru.
 *
 * Ziyaretçi hangi adresi açarsa açsın bu ekranı görür; açık kalan tek yol
 * yönetim panelidir. Kısıtın sebebi (lisans) **yazılmaz** — oyuncuya lisanstan
 * söz edilmez, yalnızca hizmetin kapalı olduğu bildirilir (aynı kural
 * backend/services/license.py içinde de geçerlidir).
 *
 * Panele giriş buradan yapılır: ``/admin`` oturumsuz açıldığında AdminLayout
 * ziyaretçiyi ``state.auth = 'login'`` ile ana sayfaya gönderir, o durum
 * burada yakalanıp giriş penceresi açılır. Giriş yapan hesap yöneticiyse
 * panele yönlendirilir; değilse ekran kapalı kalır.
 */
const SiteLockedScreen = () => {
  const settings = useSettings();
  const { user, ready, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  // Panele girmeye çalışıldı mı? Yetki uyarısı yalnızca bundan sonra çizilir:
  // oturumu açık sıradan bir oyuncuya, hiç istemediği hâlde "hesabınızın
  // yönetim yetkisi yok" demek kafa karıştırırdı.
  const [adminAttempt, setAdminAttempt] = useState(false);

  const openAdminLogin = () => {
    setAdminAttempt(true);
    setAuthOpen(true);
  };

  // AdminLayout'un yönlendirmesi (ya da doğrudan /admin/login) buraya düşer:
  // oturum yoksa giriş penceresi açılır, oturum yetkisizse gerekçe yazılır.
  // Durum tek kullanımlıktır, adres çubuğundan silinir.
  useEffect(() => {
    const wantsLogin = location.state?.auth === 'login';
    if (!wantsLogin && !location.state?.adminDenied) return;
    setAdminAttempt(true);
    if (wantsLogin && !user) setAuthOpen(true);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate, user]);

  // Yönetici giriş yaptığı anda panele geçer: kilit ekranında yapabileceği
  // başka bir şey yoktur.
  useEffect(() => {
    if (ready && user?.is_admin) navigate('/admin', { replace: true });
  }, [navigate, ready, user]);

  const deniedAccount = adminAttempt && ready && user && !user.is_admin;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[var(--pusu-bg-deep)]">
      <main className="min-h-full grid place-items-center px-4 py-10">
        <section className="pusu-panel w-full max-w-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <Logo size="lg" linked={false} />
          </div>

          <div
            className="mx-auto mb-5 grid place-items-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10"
            aria-hidden="true"
          >
            <Lock size={24} className="text-[var(--pusu-gold)]" />
          </div>

          <h1 className="text-2xl font-black mb-3">Bu sayfaya erişilemiyor</h1>
          <p className="text-sm text-gray-400 leading-relaxed">
            Site şu anda hizmet dışıdır. Bakiyeniz ve hesap bilgileriniz
            güvendedir; hizmet yeniden açıldığında kaldığınız yerden devam
            edebilirsiniz.
          </p>

          {settings.contact_email && (
            <a
              className="inline-block mt-5 text-sm gold-text"
              href={`mailto:${settings.contact_email}`}
            >
              {settings.contact_email}
            </a>
          )}

          <div className="mt-8 pt-6 border-t border-white/10">
            {deniedAccount ? (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  <span className="font-semibold text-gray-400">
                    {user.username}
                  </span>{' '}
                  hesabının yönetim paneli yetkisi yok.
                </p>
                <button
                  type="button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 text-gray-300 hover:text-white hover:border-white/25"
                >
                  Çıkış yap
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openAdminLogin}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 text-gray-300 hover:text-white hover:border-white/25"
              >
                <ShieldCheck size={15} /> Yönetici girişi
              </button>
            )}
          </div>
        </section>
      </main>

      {/* Kilitli sitede yeni üyelik alınmaz: pencere yalnızca giriş sunar. */}
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab="login"
        registrationOpen={false}
      />
    </div>
  );
};

export default SiteLockedScreen;
